<template>
    <div>
        <v-row class="mt-6">
            <v-col cols="12" lg="8">
                <v-card outlined style="background-color: transparent">
                    <v-card-title>
                        <v-list two-line color="transparent" class="pa-0" width="100%">
                            <v-list-item class="pa-0">
                                <v-list-item-avatar class="my-0">
                                    <v-icon large>date_range</v-icon>
                                </v-list-item-avatar>
                                <v-list-item-content class="py-0">
                                    <v-list-item-title class="secondary--text">Subscription</v-list-item-title>
                                    <v-list-item-subtitle>{{ status.resource_pool_start }} - {{ status.resource_pool_end }}</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action>
                                    <v-dialog v-if="isAccountManager" v-model="subscriptionDialog" width="500" persistent>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn v-bind="attrs" v-on="on" icon>
                                                <v-icon>mdi-cog</v-icon>
                                            </v-btn>
                                        </template>
                                        <v-card>
                                            <v-card-title class="flex-nowrap align-start">
                                                Change subscription dates
                                                <v-spacer></v-spacer>
                                                <v-btn @click="subscriptionDialog = false" icon><v-icon>clear</v-icon></v-btn>
                                            </v-card-title>
                                            <v-card-text class="mt-4">
                                                <date-range-picker v-model="dates" :dates="dates" :min-max="false"></date-range-picker>
                                                <label v-if="datesError" class="error--text">{{ datesError }}</label>
                                            </v-card-text>
                                            <v-card-actions>
                                                <v-spacer></v-spacer>

                                                <v-btn
                                                    color="primary"
                                                    text
                                                    :loading="updatingSubscriptionDates"
                                                    @click="updateSubscriptionDates()"
                                                    :disabled="datesError">
                                                    Save
                                                </v-btn>
                                            </v-card-actions>
                                        </v-card>
                                    </v-dialog>
                                </v-list-item-action>
                            </v-list-item>
                        </v-list>
                    </v-card-title>
                    <v-container fluid>
                        <v-row>
                            <v-col cols="12" lg="6">
                                <v-card height="100%" class="d-flex flex-column">
                                    <v-card-title>
                                        <v-icon large left>mdi-memory</v-icon>
                                        <div class="secondary--text">NCU capacity</div>
                                        <v-spacer></v-spacer>
                                        <v-menu v-model="ncuCapacityDialog" left offset-x nudge-top="10" v-if="isAccountManager">
                                            <template v-slot:activator="{ on }">
                                                <v-btn v-on="on" color="secondary" text class="ml-1">
                                                    <v-icon>more_horiz</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list dense nav>
                                                <limit-dialog
                                                    :rpid="Number($route.params.rpid)"
                                                    limitType="ncu"
                                                    activatorType="listItem"
                                                    activatorLabel="NCU limit"
                                                    @success="getStatus()"
                                                    :prefill="{
                                                        start_date: this.status.resource_pool_start,
                                                        end_date: this.status.resource_pool_end
                                                    }"></limit-dialog>
                                                <v-list-item
                                                    :to="{ name: 'ncu-limit-history', params: { rpid: $route.params.rpid } }"
                                                    @click="ncuCapacityDialog = false">
                                                    <v-list-item-icon>
                                                        <v-icon>mdi-clock-outline</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-title class="mr-5">NCU limit History</v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </v-card-title>

                                    <template v-if="!ncuStats">
                                        <v-spacer></v-spacer>
                                        <v-card-text>
                                            <dashboard-empty>This subscription has no limits set</dashboard-empty>
                                        </v-card-text>
                                        <v-spacer></v-spacer>
                                    </template>

                                    <template v-else-if="ncuStats.limit_ncuhour">
                                        <v-card-text v-if="ncuHourUsage >= 100" class="text-center pt-4">
                                            <v-avatar size="44" color="error">
                                                <v-icon dark>mdi-close</v-icon>
                                            </v-avatar>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    <v-list-item-title>Resources overused!</v-list-item-title>
                                                    <v-list-item-subtitle class="subtitle">
                                                        You have exhausted your NCU capacity, further computations on Included compute sizes will incur credit
                                                        costs.
                                                    </v-list-item-subtitle>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-card-text>

                                        <v-card-text v-else class="text-center pt-4">
                                            <v-avatar size="44" color="success">
                                                <v-icon dark>mdi-check</v-icon>
                                            </v-avatar>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    <v-list-item-title>All good!</v-list-item-title>
                                                    <v-list-item-subtitle class="subtitle">
                                                        Your NCU usage is currently within your allocated capacity.
                                                    </v-list-item-subtitle>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-card-text>

                                        <v-spacer></v-spacer>
                                        <v-card-text>
                                            <div class="px-6 py-4 ma-auto text-center text-h5 secondary--text">
                                                {{ ncuStats.limit_ncuhour?.toLocaleString() }} NCU hours
                                            </div>
                                            <div class="px-7 pb-4">
                                                <v-tooltip top>
                                                    <template v-slot:activator="{ on }">
                                                        <v-progress-linear
                                                            v-on="on"
                                                            :value="ncuHourUsage"
                                                            rounded
                                                            :color="usageColor(ncuHourUsage)"
                                                            class="my-2"
                                                            height="8"></v-progress-linear>
                                                    </template>
                                                    <span>{{ Math.floor(ncuStats.total_ncu_hours_used).toLocaleString() }} NCU hours</span>
                                                </v-tooltip>
                                                <div class="text--secondary text-center">
                                                    <strong>{{ Math.floor(ncuHourUsage).toLocaleString() }}% used</strong>
                                                </div>
                                            </div>
                                        </v-card-text>
                                    </template>

                                    <template v-else>
                                        <v-card-text class="text-center pt-4">
                                            <v-avatar size="44" color="info">
                                                <v-icon dark large>mdi-information-symbol</v-icon>
                                            </v-avatar>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    <v-list-item-subtitle class="subtitle">
                                                        You are using pay as you go, below is your current NCU usage.
                                                    </v-list-item-subtitle>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-card-text>

                                        <v-spacer></v-spacer>

                                        <v-card-text>
                                            <div class="px-6 py-4 ma-auto text-center text-h5 secondary--text">
                                                {{ Math.floor(ncuStats.total_ncu_hours_used).toLocaleString() }} NCU hours
                                            </div>
                                        </v-card-text>
                                    </template>

                                    <v-card-actions>
                                        <v-btn text @click="checkNcu()">details</v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                            <v-col cols="12" lg="6">
                                <v-card height="100%" class="d-flex flex-column">
                                    <v-card-title>
                                        <v-icon large left>mdi-file-cabinet</v-icon>
                                        <div class="secondary--text">File system capacity</div>
                                        <v-spacer></v-spacer>
                                        <v-menu v-model="fsCapacityDialog" left offset-x nudge-top="10" v-if="isAccountManager">
                                            <template v-slot:activator="{ on }">
                                                <v-btn v-on="on" color="secondary" text class="ml-1">
                                                    <v-icon>more_horiz</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list dense nav>
                                                <limit-dialog
                                                    :rpid="Number($route.params.rpid)"
                                                    limitType="fs"
                                                    activatorType="listItem"
                                                    activatorLabel="File system limit"
                                                    @success="getStatus()"
                                                    :prefill="{
                                                        start_date: this.status.resource_pool_start,
                                                        end_date: this.status.resource_pool_end
                                                    }"></limit-dialog>
                                                <v-list-item
                                                    :to="{ name: 'fs-limit-history', params: { rpid: $route.params.rpid } }"
                                                    @click="fsCapacityDialog = false">
                                                    <v-list-item-icon>
                                                        <v-icon>mdi-clock-outline</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-title class="mr-5">File system limit History</v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </v-card-title>

                                    <template v-if="!fsStats">
                                        <v-spacer></v-spacer>
                                        <v-card-text>
                                            <dashboard-empty>This subscription has no limits set</dashboard-empty>
                                        </v-card-text>
                                        <v-spacer></v-spacer>
                                    </template>

                                    <template v-else-if="fsStats.limit_bytes">
                                        <v-card-text v-if="fsUsage >= 100" class="text-center pt-4">
                                            <v-avatar size="44" color="error">
                                                <v-icon dark>mdi-close</v-icon>
                                            </v-avatar>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    <v-list-item-title>Resources overused!</v-list-item-title>
                                                    <v-list-item-subtitle class="subtitle">You have exhausted your File system capacity!</v-list-item-subtitle>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-card-text>

                                        <v-card-text v-else class="text-center pt-4">
                                            <v-avatar size="44" color="success">
                                                <v-icon dark>mdi-check</v-icon>
                                            </v-avatar>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    <v-list-item-title>All good!</v-list-item-title>
                                                    <v-list-item-subtitle class="subtitle">
                                                        Your File system usage is currently within your allocated capacity.
                                                    </v-list-item-subtitle>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-card-text>

                                        <v-spacer></v-spacer>
                                        <v-card-text>
                                            <div class="px-6 py-4 ma-auto text-center text-h5 secondary--text">{{ humanFileSize(fsStats.limit_bytes) }}</div>
                                            <div class="px-7 pb-4">
                                                <v-tooltip top>
                                                    <template v-slot:activator="{ on }">
                                                        <v-progress-linear
                                                            v-on="on"
                                                            :value="fsUsage"
                                                            rounded
                                                            :color="usageColor(fsUsage)"
                                                            class="my-2"
                                                            height="8"></v-progress-linear>
                                                    </template>
                                                    <span>{{ humanFileSize(fsStats.total_fs_bytes_used) }}</span>
                                                </v-tooltip>
                                                <div class="text--secondary text-center">
                                                    <strong>{{ Math.floor(fsUsage).toLocaleString() }}% used</strong>
                                                </div>
                                            </div>
                                        </v-card-text>
                                    </template>

                                    <template v-else>
                                        <v-card-text class="text-center pt-4">
                                            <v-avatar size="44" color="info">
                                                <v-icon dark large>mdi-information-symbol</v-icon>
                                            </v-avatar>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    <v-list-item-subtitle class="subtitle">
                                                        You are using pay as you go, below is your current storage footprint.
                                                    </v-list-item-subtitle>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-card-text>

                                        <v-spacer></v-spacer>

                                        <v-card-text>
                                            <div class="px-6 py-4 ma-auto text-center text-h5 secondary--text">
                                                {{ humanFileSize(fsStats.total_fs_bytes_used) }}
                                            </div>
                                        </v-card-text>
                                    </template>

                                    <v-card-actions>
                                        <v-btn text @click="checkFs()">details</v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card>
            </v-col>
            <v-col cols="12" lg="4">
                <v-card height="100%" class="d-flex flex-column">
                    <v-card-title>
                        <v-icon large left>payment</v-icon>
                        <div class="secondary--text">Credit balance</div>
                    </v-card-title>
                    <div class="pa-8 pt-4 ma-auto text-center text-h4 secondary--text">
                        {{ currentBalanceRounded }}
                    </div>
                    <v-card-text class="d-flex justify-center flex-wrap" style="gap: 16px">
                        <create-credit-dialog @credit-created="getStatus()"></create-credit-dialog>
                        <credit-transfer-dialog
                            :credits="status.subscription_stats.current_balance"
                            :precision="precision"
                            @transfer-success="getStatus()"></credit-transfer-dialog>
                    </v-card-text>
                    <v-card-text>
                        <v-list>
                            <v-list-item class="grey lighten-4 rounded mb-2">
                                <v-list-item-content>
                                    <v-list-item-subtitle>Initial</v-list-item-subtitle>
                                    <v-list-item-title class="text-h5 secondary--text">
                                        {{ formatAC(status.initial_credits) }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>

                            <v-list-item class="grey lighten-4 rounded my-2">
                                <v-list-item-content>
                                    <v-list-item-subtitle>Top-up</v-list-item-subtitle>
                                    <v-list-item-title class="text-h5 secondary--text">
                                        {{ formatAC(status.top_up_credits) }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>

                            <v-list-item class="grey lighten-4 rounded mt-2">
                                <v-list-item-content>
                                    <v-list-item-subtitle>Used</v-list-item-subtitle>
                                    <v-list-item-title class="text-h5 secondary--text">
                                        {{ formatAC(status.total_credits_used) }}
                                    </v-list-item-title>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-card-text>
                    <v-card-actions class="flex-wrap">
                        <v-btn text @click="checkCredit()">details</v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>

        <v-row class="mt-10">
            <v-col cols="12">
                <v-card flat color="transparent">
                    <v-card-title>
                        <v-icon large left>mdi-order-bool-ascending-variant</v-icon>
                        <div class="secondary--text">Resting storage</div>
                    </v-card-title>
                    <v-container fluid>
                        <v-row>
                            <v-col cols="12" lg="4">
                                <v-card height="100%" class="d-flex flex-column">
                                    <v-card-title>
                                        <v-icon large left>mdi-sleep</v-icon>
                                        <div class="secondary--text">Resting capacity</div>
                                        <v-spacer></v-spacer>
                                        <v-menu v-model="restingCapacityDialog" left offset-x nudge-top="10" v-if="isAccountManager">
                                            <template v-slot:activator="{ on }">
                                                <v-btn v-on="on" color="secondary" text class="ml-1">
                                                    <v-icon>more_horiz</v-icon>
                                                </v-btn>
                                            </template>
                                            <v-list dense nav>
                                                <limit-dialog
                                                    :rpid="Number($route.params.rpid)"
                                                    limitType="resting"
                                                    activatorType="listItem"
                                                    activatorLabel="Resting limit"
                                                    @success="getStatus()"
                                                    :prefill="{
                                                        start_date: this.status.resource_pool_start,
                                                        end_date: this.status.resource_pool_end
                                                    }"></limit-dialog>
                                                <v-list-item
                                                    :to="{ name: 'resting-limit-history', params: { rpid: $route.params.rpid } }"
                                                    @click="restingCapacityDialog = false">
                                                    <v-list-item-icon>
                                                        <v-icon>mdi-clock-outline</v-icon>
                                                    </v-list-item-icon>
                                                    <v-list-item-title class="mr-5">Resting limit History</v-list-item-title>
                                                </v-list-item>
                                            </v-list>
                                        </v-menu>
                                    </v-card-title>

                                    <template v-if="!restingStats">
                                        <v-spacer></v-spacer>
                                        <v-card-text>
                                            <dashboard-empty>This subscription has no limits set</dashboard-empty>
                                        </v-card-text>
                                        <v-spacer></v-spacer>
                                    </template>

                                    <template v-else-if="restingStats.limit_bytes">
                                        <v-card-text v-if="restingUsage >= 100" class="text-center pt-4">
                                            <v-avatar size="44" color="error">
                                                <v-icon dark>mdi-close</v-icon>
                                            </v-avatar>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    <v-list-item-title>Resources overused!</v-list-item-title>
                                                    <v-list-item-subtitle class="subtitle">
                                                        You have exhausted your Resting capacity, you wont be able to put projects to rest until you free up
                                                        some space.
                                                    </v-list-item-subtitle>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-card-text>

                                        <v-card-text class="text-center pt-4" v-else>
                                            <v-avatar size="44" color="success">
                                                <v-icon dark>mdi-check</v-icon>
                                            </v-avatar>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    <v-list-item-title>All good!</v-list-item-title>
                                                    <v-list-item-subtitle class="subtitle">
                                                        Your Resting usage is currently within your allocated capacity.
                                                    </v-list-item-subtitle>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-card-text>

                                        <v-spacer></v-spacer>

                                        <v-card-text>
                                            <div class="px-6 py-4 ma-auto text-center text-h5 secondary--text">
                                                {{ humanFileSize(restingStats.limit_bytes) }}
                                            </div>
                                            <div class="px-7 pb-4">
                                                <v-tooltip top>
                                                    <template v-slot:activator="{ on }">
                                                        <v-progress-linear
                                                            v-on="on"
                                                            :value="restingUsage"
                                                            rounded
                                                            :color="usageColor(restingUsage)"
                                                            class="my-2"
                                                            height="8"></v-progress-linear>
                                                    </template>
                                                    <span>{{ humanFileSize(restingStats.total_resting_bytes_used) }}</span>
                                                </v-tooltip>
                                                <div class="text--secondary text-center">
                                                    <strong>{{ Math.floor(restingUsage).toLocaleString() }}% used</strong>
                                                </div>
                                            </div>
                                        </v-card-text>
                                    </template>

                                    <template v-else>
                                        <v-card-text class="text-center pt-4">
                                            <v-avatar size="44" color="info">
                                                <v-icon dark large>mdi-information-symbol</v-icon>
                                            </v-avatar>
                                            <v-list-item>
                                                <v-list-item-content>
                                                    <v-list-item-subtitle class="subtitle">
                                                        You are using pay as you go, below is your current storage footprint.
                                                    </v-list-item-subtitle>
                                                </v-list-item-content>
                                            </v-list-item>
                                        </v-card-text>

                                        <v-spacer></v-spacer>

                                        <v-card-text>
                                            <div class="px-6 py-4 ma-auto text-center text-h5 secondary--text">
                                                {{ humanFileSize(restingStats.total_resting_bytes_used) }}
                                            </div>
                                        </v-card-text>
                                    </template>

                                    <v-card-actions>
                                        <v-btn text @click="checkResting()">details</v-btn>
                                    </v-card-actions>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card>
            </v-col>
        </v-row>

        <v-row class="mt-10">
            <v-col cols="12">
                <v-card flat color="transparent">
                    <v-card-title>
                        <v-icon large left>mdi-puzzle-outline</v-icon>
                        <div class="secondary--text">Additional services</div>
                    </v-card-title>
                    <v-container fluid>
                        <v-row>
                            <v-col cols="4" v-for="(value, service) in status.activated_services" :key="service">
                                <v-card flat>
                                    <v-list two-line color="transparent">
                                        <v-list-item>
                                            <v-list-item-avatar>
                                                <v-icon v-if="value" class="primary lighten-1" dark>mdi-check</v-icon>
                                                <v-icon v-else class="grey lighten-1" dark>mdi-circle-outline</v-icon>
                                            </v-list-item-avatar>
                                            <v-list-item-content>
                                                <v-list-item-title>{{ services[service].label }}</v-list-item-title>
                                                <v-list-item-subtitle>{{ services[service].description }}</v-list-item-subtitle>
                                            </v-list-item-content>
                                            <v-list-item-action>
                                                <v-btn text color="primary" v-if="!value" @click="toggleService(service)" :disabled="!rpManager">enable</v-btn>
                                                <v-btn text color="secondary" disabled v-else>Enabled</v-btn>
                                            </v-list-item-action>
                                        </v-list-item>
                                    </v-list>
                                </v-card>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card>
            </v-col>
        </v-row>
        <v-row id="large-file-storage" class="mt-10">
            <v-col cols="12">
                <additional-storage :rpid="Number($route.params.rpid)" :expired="utilStatus === 'expired'" :selectedRP="selectedRP" @refresh="getStatus" />
            </v-col>
        </v-row>
    </div>
</template>

<script>
import eventBus from '@/components/EventBus'
import { toLocaleFixed, humanFileSize } from '@/utils'
import DateRangePicker from '@/components/DateRangePicker.vue'
import { mapState } from 'vuex'
import { DateTime } from 'luxon'

const LimitDialog = () => import('@/modules/user/components/LimitDialog.vue')
const DashboardEmpty = () => import('@/components/DashboardEmpty.vue')
const AdditionalStorage = () => import('./AdditionalStorage.vue')
const CreditTransferDialog = () => import('@/modules/user/components/CreditTransferDialog.vue')
const CreateCreditDialog = () => import('@/modules/user/components/CreateCreditDialog.vue')

export default {
    name: 'MonitoringOverview',
    components: { AdditionalStorage, DateRangePicker, LimitDialog, DashboardEmpty, CreditTransferDialog, CreateCreditDialog },
    data() {
        return {
            fetching: {
                status: false
            },
            utilStatus: null,
            status: null,
            ncuStats: null,
            fsStats: null,
            restingStats: null,
            services: {
                video: {
                    label: 'Video',
                    description: 'Video library uploading',
                    enableFlag: 'vimeo_enabled',
                    overrideFlag: 'video_enabled_override'
                },
                db: {
                    label: 'Database',
                    description: 'Use databases and query data',
                    enableFlag: 'db_enabled',
                    overrideFlag: 'tables_enabled_override'
                },
                hpc: {
                    label: 'Credit-base sizes',
                    description: 'Enable usage of credit-based sizes',
                    enableFlag: 'hpc_enabled',
                    overrideFlag: 'hpc_enabled_override'
                }
            },
            serviceToBeActivated: '',
            precision: this.$appConfig?.VUE_APP_DASHBOARD_PRECISION,
            updatingSubscriptionDates: false,
            transferResult: null,
            subscriptionDialog: false,
            dates: [],
            datesError: null,
            ncuCapacityDialog: false,
            fsCapacityDialog: false,
            restingCapacityDialog: false
        }
    },
    computed: {
        ...mapState('userStore', ['isAccountManager', 'rpManager', 'selectedRP']),
        currentBalanceRounded() {
            return this.formatAC(this.status.subscription_stats.current_balance)
        },
        ncuHourUsage() {
            if (!this.ncuStats.total_ncu_hours_used_pct) return 0
            return this.ncuStats.total_ncu_hours_used_pct * 100
        },
        fsUsage() {
            if (!this.fsStats.total_fs_bytes_used_pct) return 0
            return this.fsStats.total_fs_bytes_used_pct * 100
        },
        restingUsage() {
            if (!this.restingStats.total_resting_bytes_used_pct) return 0
            return this.restingStats.total_resting_bytes_used_pct * 100
        }
    },
    methods: {
        usageColor(usage) {
            return usage < 60 ? 'success' : usage < 90 ? 'amber' : 'error'
        },
        async getStatus() {
            try {
                this.fetching.status = true
                this.$emit('fetching-charts', true)
                const { data } = await this.$axios.get(`/resource/${this.$route.params.rpid}/status`)
                this.status = data
                this.ncuStats = data.subscription_stats.ncu_limit
                this.fsStats = data.subscription_stats.fs_limit
                this.restingStats = data.subscription_stats.resting_limit
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Could not fetch utilization data.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            } finally {
                this.fetching.status = false
                this.$emit('fetching-charts', false)
            }
        },
        toggleService(service) {
            this.$store.dispatch('showGlobalDialog', {
                dialogTitle: `Enabling ${this.services[service].label} service`,
                dialogText: `By enabling the ${this.services[service].label} feature, organization manager(s) will be able to enable it in the organization as well. Please note that the feature cannot be disabled at resource pool level anymore once enabled, only organization level.`,
                dialogAction: ['cancel', 'accept']
            })
            this.serviceToBeActivated = service
        },
        async activateService(service) {
            const serviceLabel = this.services[service].label
            try {
                await this.$axios.patch(`/resource/${this.$route.params.rpid}`, {
                    [`${this.services[service].enableFlag}`]: true
                })
                this.getStatus()
                this.$store.dispatch('showSnackBar', {
                    snackBarText: `Successfully enabled ${serviceLabel} service`,
                    snackBarTimeout: 5000,
                    snackBarIcon: 'check_circle'
                })
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: `Could not activate ${serviceLabel} service. Please contact Nuvolos support if the error persists.`,
                    snackBarTimeout: 10000,
                    snackBarIcon: 'error'
                })
            }
            this.serviceToBeActivated = ''
        },
        formatAC(value, precision = this.precision) {
            return toLocaleFixed(value, precision)
        },
        async updateSubscriptionDates() {
            this.updatingSubscriptionDates = true
            try {
                await this.$axios.patch(`/resource/${this.$route.params.rpid}/edit`, {
                    new_rp_start: this.dates[0],
                    new_rp_end: this.dates[1]
                })
                this.subscriptionDialog = false
                this.getStatus()
            } catch (error) {
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Failed to update subscription dates.',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'error'
                })
            }
            this.updatingSubscriptionDates = false
        },
        humanFileSize(bytes, si) {
            return humanFileSize(bytes, si)
        },
        checkNcu() {
            this.$router.push({
                name: 'reporting-rp-plan-utilization',
                params: { rpid: this.$route.params.rpid, startDate: this.ncuStats.start_date, endDate: this.ncuStats.end_date }
            })
        },
        checkFs() {
            this.$router.push({
                name: 'reporting-rp-plan-utilization',
                params: { rpid: this.$route.params.rpid, startDate: this.fsStats.start_date, endDate: this.fsStats.end_date },
                hash: '#fs'
            })
        },
        checkCredit() {
            this.$router.push({
                name: 'reporting-rp-credit-utilization',
                params: { rpid: this.$route.params.rpid }
            })
        },
        checkResting() {
            this.$router.push({
                name: 'reporting-rp-plan-utilization',
                params: { rpid: this.$route.params.rpid, startDate: this.restingStats.start_date, endDate: this.restingStats.end_date },
                hash: '#resting'
            })
        }
    },
    watch: {
        dates(dates) {
            if (dates) {
                const [startDate, endDate] = dates
                this.datesError = null
                if (DateTime.fromISO(startDate) >= DateTime.fromISO(endDate)) {
                    this.datesError = 'Start date must be earlier than end date.'
                }
            }
        },
        $route: {
            handler(to) {
                if (to.params.rpid) {
                    this.getStatus()
                }
            },
            immediate: true
        },
        subscriptionDialog: {
            handler() {
                const monthStart = this.status.resource_pool_start
                const monthEnd = this.status.resource_pool_end

                this.dates = [monthStart, monthEnd]
            },
            immediate: true
        }
    },
    mounted() {
        eventBus.$on('global-dialog-accept', () => {
            if (this.serviceToBeActivated) this.activateService(this.serviceToBeActivated)
        })
    },
    destroyed() {
        eventBus.$off('global-dialog-accept')
    }
}
</script>
<style scoped lang="scss">
.subtitle {
    white-space: normal;
}
</style>
